export function downloadJSON(schema, filename) {
  if (!schema || typeof schema !== 'object') return;

  // Recursively clean `type` by removing "null"
  const cleanSchema = (node) => {
    if (Array.isArray(node)) {
      return node.map(cleanSchema);
    } else if (node && typeof node === 'object') {
      const result = {};
      for (const key in node) {
        if (key === 'type' && Array.isArray(node[key])) {
          result[key] = node[key].filter((t) => t !== 'null');
        } else {
          result[key] = cleanSchema(node[key]);
        }
      }
      return result;
    }
    return node;
  };

  const cleaned = cleanSchema(schema);

  const blob = new Blob([JSON.stringify(cleaned, null, 4)], {
    type: 'application/json',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

export function downloadCSV(schema, filename) {
  if (!schema || typeof schema !== 'object') return;

  const rows = [['Property path', 'Type', 'Required', 'Constraints', 'Description']];

  // Helper to get the type of a value
  const getType = (value) => {
    if (!value) return '—';
    if (Array.isArray(value.type)) return value.type.join(' or ');
    if (value.type === 'array') {
      const item = value.items || {};
      return item.title
        ? `array of ${item.title}`
        : `array of ${item.type || ''}${value.format ? `: ${value.format}` : ''}`;
    }
    if (value.type === 'object' && value.title) return value.title;
    return value.type;
  };

  const getConstraints = (value) => {
    if (!value) return '—';
    const parts = [];
    const target = value.type === 'array' && value.items ? value.items : value;

    if (target.const !== undefined) parts.push(`Const: ${target.const}`);
    if (Array.isArray(target.enum)) parts.push(`Enum: [${target.enum.join(', ')}]`);
    if (target.format) parts.push(`Format: ${target.format}`);
    if (target.pattern) parts.push(`Pattern: ${target.pattern}`);
    if (target.minLength !== undefined) parts.push(`minLength: ${target.minLength}`);
    if (target.maxLength !== undefined) parts.push(`maxLength: ${target.maxLength}`);
    if (target.minimum !== undefined) parts.push(`minimum: ${target.minimum}`);
    if (target.maximum !== undefined) parts.push(`maximum: ${target.maximum}`);
    if (target.minItems !== undefined) parts.push(`minItems: ${target.minItems}`);
    if (target.maxItems !== undefined) parts.push(`maxItems: ${target.maxItems}`);
    if (target.uniqueItems) parts.push('uniqueItems: true');

    return parts.length > 0 ? parts.join(', ') : '—';
  };

  const walk = (node, path = '$') => {
    if (!node?.properties) return;

    const requiredProps = node.required || [];

    for (const key of Object.keys(node.properties)) {
      const value = node.properties[key];
      const isRequired = requiredProps.includes(key);
      const propPath = `${path}.${key}${value.type === 'array' ? '[*]' : ''}`;

      rows.push([
        propPath,
        getType(value),
        isRequired ? 'Yes' : 'No',
        getConstraints(value),
        value.description || '',
      ]);

      if (value.type === 'object' || (value.type === 'array' && value.items?.type === 'object')) {
        walk(value.type === 'array' ? value.items : value, propPath);
      }
    }
  };

  walk(schema, '$');

  const escapeCSV = (value) => `"${String(value).replace(/"/g, '""')}"`;

  const csvContent = rows
    .map((row, rowIndex) =>
      row
        .map((value, colIndex) => {
          if (rowIndex === 0) return escapeCSV(value);

          if (colIndex === 0 && (value.includes('Event') || value.includes('Resource'))) {
            return escapeCSV(value);
          }
          return escapeCSV(value);
        })
        .join(',')
    )
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
